import { FileText, Paperclip } from "lucide-react";
import type * as React from "react";
import { cn } from "../lib/cn";
import { MessageBody } from "./MessageBody";
import { MessageBubble } from "./MessageBubble";

/**
 * SharedConversationTurns — read-only rendering of a frozen share.
 *
 * One component for both surfaces that show a shared thread: the client's
 * "Condivisi" drawer and the public ``/share/[token]`` page on apps/web.
 * SSR-safe by construction (MessageBubble + MessageBody only, no i18n
 * runtime, no browser globals at import time) so it can live in
 * ``@ando/ui/read``. Sources and attachments come as labels only — the
 * BE never puts tenant ids in a snapshot — and are rendered as muted
 * chips under the turn, icon-only so no string needs translating here.
 */

export interface SharedTurn {
  readonly role: "user" | "assistant";
  readonly content: string;
  readonly timestamp?: string | null;
  readonly sources?: ReadonlyArray<{ readonly label: string; readonly source_type: string }>;
  readonly attachments?: ReadonlyArray<string>;
}

export interface SharedConversationTurnsProps {
  readonly turns: ReadonlyArray<SharedTurn>;
  readonly className?: string;
}

const Chips: React.FC<{
  readonly icon: React.ReactNode;
  readonly items: ReadonlyArray<string>;
  readonly align: "left" | "right";
}> = ({ icon, items, align }) => (
  <ul
    className={cn(
      "flex flex-wrap gap-1 text-2xs text-ink-3",
      align === "right" && "justify-end",
    )}
  >
    {items.map((label) => (
      <li
        key={label}
        className="inline-flex max-w-[260px] items-center gap-1 rounded-sm border border-hairline px-1.5 py-0.5 leading-none"
      >
        <span aria-hidden className="shrink-0">
          {icon}
        </span>
        <span className="truncate">{label}</span>
      </li>
    ))}
  </ul>
);

export const SharedConversationTurns: React.FC<SharedConversationTurnsProps> = ({
  turns,
  className,
}) => (
  <div className={cn("flex flex-col gap-5", className)}>
    {turns.map((turn, i) => {
      const isUser = turn.role === "user";
      const sources = (turn.sources ?? []).map((s) => s.label);
      const attachments = turn.attachments ?? [];
      const footer =
        sources.length > 0 || attachments.length > 0 ? (
          <div className={cn("flex flex-col gap-1", isUser && "items-end")}>
            {attachments.length > 0 ? (
              <Chips
                icon={<Paperclip size={10} strokeWidth={1.75} />}
                items={attachments}
                align={isUser ? "right" : "left"}
              />
            ) : null}
            {sources.length > 0 ? (
              <Chips
                icon={<FileText size={10} strokeWidth={1.75} />}
                items={sources}
                align={isUser ? "right" : "left"}
              />
            ) : null}
          </div>
        ) : null;
      return (
        <MessageBubble
          // Frozen snapshots have no stable id; index + role is unique here.
          key={`${i}-${turn.role}`}
          variant={isUser ? "user" : "agent"}
          data-no-animate=""
          footer={footer}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap break-words">{turn.content}</p>
          ) : (
            <MessageBody markdown={turn.content} />
          )}
        </MessageBubble>
      );
    })}
  </div>
);
SharedConversationTurns.displayName = "SharedConversationTurns";
